import { inject } from '@angular/core';
import { Router, type Routes } from '@angular/router';
import { authorizeGuard } from '@core/guards';

/**
 * The fall-through redirect, as a function rather than a string.
 *
 * `redirectTo: 'error?reason=invalid_request'` DOES NOT WORK. `redirectTo` is a path, not a URL:
 * Angular never parses a query string out of it, so the reason was dropped and the visitor landed
 * on a bare `/error`. `copyForError(null)` then falls back to UNKNOWN_ERROR_COPY -- "Something went
 * wrong / Try again. If it keeps happening, contact support." -- which sends someone whose only
 * mistake was opening the site directly to the support queue.
 *
 * A `RedirectFunction` runs in an injection context and may return a UrlTree, which is the one
 * form that carries query parameters.
 */
const toInvalidRequest = () =>
  inject(Router).createUrlTree(['/error'], { queryParams: { reason: 'invalid_request' } });

/**
 * Every route is lazy. The sign-in surface is the first thing a user of any Gozem application sees,
 * so the initial bundle is kept to the shell plus the screen actually needed — which is why the
 * production budget here (350 kB) is tighter than the merchant app's (500 kB).
 */
export const routes: Routes = [
  {
    /**
     * WHERE THE SERVICE REDIRECTS TO. `GET /api/v1/sso/authorize` answers
     * `302 -> /login?req=req_...`, so this is the route a real sign-in actually lands on.
     */
    path: 'login',
    canActivate: [authorizeGuard],
    loadComponent: () => import('@features/sign-in/sign-in.page').then((m) => m.SignInPage),
  },
  {
    // The direct entry, for an application that links here with the OAuth parameters rather than at
    // the service. The page bounces to the service to obtain a `req` handle.
    path: 'authorize',
    canActivate: [authorizeGuard],
    loadComponent: () => import('@features/sign-in/sign-in.page').then((m) => m.SignInPage),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('@features/sign-in/pages/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('@features/sign-in/pages/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    // Where the `no_access_to_application` terminal sends the user. UC-04 requires that screen to
    // offer a next step, and until this route existed its link 404'd.
    path: 'applications',
    loadComponent: () =>
      import('@features/applications/applications.page').then((m) => m.ApplicationsPage),
  },
  {
    // Where `no_merchant_account`, `account_disabled` and `not_implemented` send the user.
    path: 'support',
    loadComponent: () => import('@features/support/support.page').then((m) => m.SupportPage),
  },
  {
    // Where the guard sends a request it will not render a form for.
    path: 'error',
    loadComponent: () => import('@features/sign-in/pages/error.page').then((m) => m.ErrorPage),
  },
  // A bare visit to accounts.gozem.co is not a sign-in request: there is no application to return
  // to, so there is nothing useful to show. Explaining that beats an empty form.
  { path: '', pathMatch: 'full', redirectTo: toInvalidRequest },
  { path: '**', redirectTo: toInvalidRequest },
];
